/* eslint-env browser */
import { Product } from "@transfr-inc/dashboard-components/constants";
import { Role } from "../constants/role";
import { parseJwt } from "../utils/browser";

/**
 * Configuration for xAPI tracking service
 */
export const XAPI_CONFIG = {
  API_ENDPOINT: process.env.LRS_ENDPOINT + "events/",
  ENABLED: process.env.XAPI_ENABLED?.toUpperCase() === "TRUE" || false,
  APPLICATION_NAME: "Internal Portal",
  DEFAULT_USER_ROLE: Role.CS_AUTHOR.id,
};

/**
 * Gets device information.
 * @returns {Object} Device information object
 */
export const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  const screen = window.screen;

  // Simple browser detection
  let browserName = "Unknown";
  let browserVersion = "Unknown";

  if (userAgent.includes("Chrome")) {
    browserName = "Chrome";
    const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : "Unknown";
  } else if (userAgent.includes("Firefox")) {
    browserName = "Firefox";
    const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : "Unknown";
  } else if (userAgent.includes("Safari")) {
    browserName = "Safari";
    const match = userAgent.match(/Version\/(\d+\.\d+)/);
    browserVersion = match ? match[1] : "Unknown";
  }

  // Simple OS detection
  let osName = "Unknown";
  let osVersion = "Unknown";

  if (userAgent.includes("Mac OS X")) {
    osName = "macOS";
    const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
    osVersion = match ? match[1].replace("_", ".") : "Unknown";
  } else if (userAgent.includes("Windows")) {
    osName = "Windows";
    const match = userAgent.match(/Windows NT (\d+\.\d+)/);
    osVersion = match ? match[1] : "Unknown";
  } else if (userAgent.includes("Linux")) {
    osName = "Linux";
  }

  return {
    device_type: "web",
    device_access_channel: "browser",
    device_os: {
      name: osName,
      version: osVersion,
    },
    device_model: navigator.platform || "Unknown",
    device_browser: {
      name: browserName,
      version: browserVersion,
    },
    device_screen: {
      width: screen.width,
      height: screen.height,
      pixel_density: window.devicePixelRatio || 1,
    },
  };
};

/**
 * xAPITrackingService provides a single point of entry for xAPI tracking needs.
 * It manages xAPI tracking for learning analytics.
 */
class xAPITrackingService {
  constructor() {
    this._apiEndpoint = "";
    this._enabled = false;
    this._userId = "";
    this._sessionId = "";
    this._userRoleId = "";
    this._productId = "";
    this._initialized = false;

    this._enabled = XAPI_CONFIG.ENABLED;
    this._apiEndpoint = XAPI_CONFIG.API_ENDPOINT;
    if (this._enabled && !this._apiEndpoint) {
      this._enabled = false;
      console.error("xAPI endpoint not configured. Disabling xAPI tracking.");
    }
  }

  /**
   * Configures the xAPITrackingService with user and context information.
   * @param {Object} currentUser - Current user information
   */
  configure(currentUser) {
    // Prevent reconfiguration if already initialized
    if (this._initialized) {
      return;
    }

    // Try different possible user ID field names
    const userId = currentUser?.userId;
    this.setUserId(userId);
    this.setUserRoleId(currentUser);

    // Try different possible session ID field names
    const sessionId =
      currentUser.sessionId || currentUser.userId || Date.now().toString();
    this.setSessionId(sessionId);

    this._initialized = true;
  }

  /**
   * Sets the user id for the current session.
   * @param {string} userId - New user id
   */
  setUserId(userId) {
    this._userId = userId;
  }

  /**
   * Sets the user role id for the current session.
   * @param {string} currentUser - Current user
   */
  setUserRoleId(currentUser) {
    const token = parseJwt(currentUser.token);
    const userRoleId = token?.act;
    this._userRoleId = userRoleId.toString();
  }

  /**
   * Sets the session id for the current session.
   * @param {string} sessionId - New session id
   */
  setSessionId(sessionId) {
    this._sessionId = sessionId;
  }

  /**
   * Checks if the tracking service has been properly initialized.
   * @returns {boolean} True if initialized and ready, false otherwise
   */
  isInitialized() {
    return this._initialized;
  }

  /**
   * Checks if xAPI tracking is available and configured.
   * @returns {boolean} True if xAPI is available, false otherwise
   */
  isXAPIAvailable() {
    return this._enabled && this._apiEndpoint;
  }

  /**
   * Checks if xAPI tracking is enabled.
   * @returns {boolean} True if xAPI tracking is enabled, false otherwise
   */
  isXAPIEnabled() {
    return this._enabled;
  }

  /**
   * Validates event data before sending
   * @private
   * @param {Object} eventData - Event data to validate
   * @returns {boolean} True if valid, false otherwise
   */
  _validateEventData(eventData) {
    const requiredFields = [
      "event_type",
      "user_id",
      "object_id",
      "object_name",
      "object_type",
    ];

    for (const field of requiredFields) {
      if (!eventData[field]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Builds event data object from event class or parameters
   * @private
   * @param {string|Object} eventTypeOrClass - Event type string or event class instance
   * @param {Object} objectInfo - Object information (ignored if first param is event class)
   * @param {Object} options - Additional options (ignored if first param is event class)
   * @returns {Object|null} Complete event data object or null if invalid
   */
  _buildEventData(eventTypeOrClass, objectInfo, options = {}) {
    let eventType, _objectInfo, _options;

    if (typeof eventTypeOrClass === "object" && eventTypeOrClass.getEventData) {
      const eventData = eventTypeOrClass.getEventData();
      eventType = eventData.eventType;
      _objectInfo = eventData.objectInfo;
      _options = eventData.options;
    } else {
      eventType = eventTypeOrClass;
      _objectInfo = objectInfo;
      _options = options;
    }

    const deviceInfo = getDeviceInfo();

    const eventData = {
      event_type: eventType,
      user_id: this._userId,
      session_id: this._sessionId,
      timestamp: new Date().toISOString(),
      object_id: _objectInfo.id,
      object_name: _objectInfo.name,
      object_type: _objectInfo.type,
      user_role_id: this._userRoleId,
      ...deviceInfo,
      organization_id: _options.organizationId || undefined,
      classroom_id: _options.classroomId || this._classroomId,
      product_id: _options.productId || this._productId,
      application_name: XAPI_CONFIG.APPLICATION_NAME,
      target_type: _options.targetType || undefined,
      target_id: _options.targetId || undefined,
    };

    if (_options.results) {
      eventData.results = {};

      if (_options.results.success !== undefined) {
        eventData.results.success = _options.results.success;
      }

      if (_options.results.completed !== undefined) {
        eventData.results.completed = _options.results.completed;
      }
    }

    if (_options.objectExtensions) {
      eventData.object_extensions = _options.objectExtensions;
    }

    if (_options.resultExtensions) {
      eventData.result_extensions = _options.resultExtensions;
    }

    if (!this._validateEventData(eventData)) {
      return;
    }

    return eventData;
  }

  /**
   * Sends an xAPI event to the API endpoint.
   * @private
   * @param {Object} eventData - The xAPI event data
   */
  async _sendEvent(eventData) {
    if (this._enabled && !this._apiEndpoint) {
      this._enabled = false;
      console.error("xAPI endpoint not configured. Disabling xAPI tracking.");
      return;
    }

    try {
      const response = await fetch(this._apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(eventData),
      });

      if (!response.ok) {
        console.error(
          `Failed to send xAPI event (${eventData.event_type}):`,
          response.status,
          response.statusText,
          eventData
        );
      }
    } catch (error) {
      console.error(
        `Error sending xAPI event (${eventData.event_type}):`,
        error,
        eventData
      );
    }
  }

  /**
   * Sends multiple xAPI events as a batch to the API endpoint.
   * @private
   * @param {Array} eventsData - Array of xAPI event data objects
   */
  async _sendBatchEvents(eventsData) {
    if (this._enabled && !this._apiEndpoint) {
      this._enabled = false;
      console.error("xAPI endpoint not configured. Disabling xAPI tracking.");
      return;
    }

    try {
      const batchEndpoint = this._apiEndpoint + "batch/";
      const response = await fetch(batchEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(eventsData),
      });

      if (!response.ok) {
        console.error(
          "Failed to send xAPI batch events:",
          response.status,
          response.statusText
        );
      }
    } catch (error) {
      console.error("Error sending xAPI batch events:", error);
    }
  }

  /**
   * Tracks an xAPI learning event based on the CSV specification.
   * @param {string|Object} eventTypeOrClass - The type of event (authenticated, viewed, completed, etc.) or an xAPI event class instance
   * @param {Object} objectInfo - Information about the learning object (ignored if first param is event class)
   * @param {Object} options - Additional options (ignored if first param is event class)
   */
  async trackEvent(eventTypeOrClass, objectInfo, options = {}) {
    if (!this.isXAPIAvailable()) {
      return;
    }

    const eventData = this._buildEventData(
      eventTypeOrClass,
      objectInfo,
      options
    );
    if (eventData) {
      await this._sendEvent(eventData);
    }
  }

  /**
   * Tracks multiple xAPI events, automatically choosing between batch and individual calls.
   * @param {Array} events - Array of event class instances or event data objects
   * @param {boolean} useBatch - Whether to use batch API when multiple events (default: true)
   */
  async trackEvents(events, useBatch = true) {
    if (!this.isXAPIAvailable() || !events || events.length === 0) {
      return;
    }

    const validEventsData = events
      .map((event) =>
        this._buildEventData(event.eventType, event.objectInfo, event.options)
      )
      .filter((eventData) => eventData !== null);

    if (validEventsData.length === 0) {
      return;
    }

    if (useBatch && validEventsData.length > 1) {
      await this._sendBatchEvents(validEventsData);
    } else {
      const eventPromises = validEventsData.map((eventData) =>
        this._sendEvent(eventData)
      );
      await Promise.allSettled(eventPromises);
    }
  }
}

// Create a singleton instance
const xAPITracker = new xAPITrackingService();

/**
 * Convenience functions for common tracking scenarios
 */

/**
 * Track generic activity
 */
export async function trackActivity(eventType, objectInfo, options) {
  return await xAPITracker.trackEvent(eventType, objectInfo, options);
}

/**
 * Track multiple program assignments
 */
export async function trackProgramsAssigned(
  programAssignments,
  organizationId
) {
  if (!programAssignments || programAssignments.length === 0) {
    return;
  }

  const events = programAssignments.map((assignment) => ({
    eventType: "assigned",
    objectInfo: {
      id: `${assignment.id}`,
      name: assignment.data?.title || assignment.data?.name || assignment.text,
      type: "program",
    },
    options: {
      targetType: "organization",
      targetId: `${organizationId}`,
      organizationId: `${organizationId}`,
      productId: `${Product.PROG}`,
    },
  }));

  return await xAPITracker.trackEvents(events, true);
}

/**
 * Get the xAPI tracker instance for advanced usage
 */
export function getXAPITracker() {
  return xAPITracker;
}
